import { httpClient } from '@/shared/api/httpClient'

/**
 * Result of /api/telegram/mini-app/auth endpoint
 */
export interface TelegramEmailFlow {
    screen: 'confirm_email' | 'email' | 'code' | 'error' | 'protected_account'
    email?: string
    revision?: string
    error?: string
    can_skip?: boolean
    changing_email?: boolean
}

export type TelegramEmailAction = 'send_code' | 'resend' | 'change_email' | 'cancel_change' | 'skip' | 'cancel'

export interface TelegramAuthResult {
    result: 'success' | 'sponsor_required' | 'totp_required' | 'email_required' | 'error'
    email_flow?: TelegramEmailFlow
    /** Encrypted auth token (present on success or totp_required) */
    spa_auth_token?: string
    /** Telegram user info (present when sponsor_required) */
    telegram_user?: {
        id: number
        username?: string
    }
    /** Session ID for TOTP verification */
    session_id?: string
    /** User ID on success */
    user_id?: number
    /** Error message */
    message?: string
    /** Error code */
    code?: number
}

/**
 * Result of /api/telegram/mini-app/register endpoint
 */
export type TelegramRegisterResult = TelegramAuthResult

/**
 * Result of /api/telegram/mini-app/verify-totp endpoint
 */
export interface TelegramVerifyTotpResult {
    result: 'success' | 'error'
    spa_auth_token?: string
    user_id?: number
    message?: string
}

/**
 * Result of /api/auth/exchange-token endpoint
 * API returns: { result: 'success', data: { success: true, token: '...' } }
 */
export interface ExchangeTokenResult {
    result: string
    data?: {
        success?: boolean
        token?: string
    }
    hash?: string
    token?: string
}

/**
 * Custom headers for Mini-App requests
 * Used by backend to enable sliding window for TOTP TTL refresh
 */
const MINI_APP_HEADERS = {
    'X-Telegram-Mini-App': '1',
}

function signedMiniAppHeaders(initData: string) {
    return { ...MINI_APP_HEADERS, 'X-Telegram-Init-Data': initData }
}

/**
 * Service for Telegram Mini-App authentication.
 *
 * Handles the complete authentication lifecycle:
 * 1. Authenticate with initData → success / sponsor_required / email_required / totp_required
 * 2. Register new user with sponsor login
 * 3. Verify TOTP code for 2FA users
 * 4. Exchange spa_auth_token for session hash
 *
 * @example
 * ```ts
 * import { TelegramAuthService } from '@/mini-app/TelegramAuthService'
 *
 * const result = await TelegramAuthService.authenticate(initData)
 *
 * if (result.result === 'success') {
 *   const token = await TelegramAuthService.exchangeToken(result.spa_auth_token!)
 *   localStorage.setItem('authToken', token)
 * }
 * ```
 */
export class TelegramAuthService {
    /**
     * Authenticate user via Telegram initData.
     *
     * Possible results:
     * - `success`: User found and authenticated, spa_auth_token provided
     * - `sponsor_required`: New user, need to show sponsor input modal
     * - `email_required`: Continue the server-owned email confirmation step
     * - `totp_required`: User has 2FA, need to verify TOTP code
     * - `error`: Authentication failed
     *
     * @param initData Raw initData string from Telegram WebApp
     * @returns Authentication result
     */
    static async authenticate(initData: string): Promise<TelegramAuthResult> {
        return httpClient.post<TelegramAuthResult>(
            '/telegram/mini-app/auth',
            {},
            { headers: signedMiniAppHeaders(initData) },
        )
    }

    /**
     * Register new user with sponsor.
     *
     * Called when authenticate() returns sponsor_required.
     * Stores the sponsor and continues the server-owned email registration flow.
     *
     * @param initData Raw initData string from Telegram WebApp
     * @param sponsorLogin Login of the sponsoring user
     * @returns Registration result with spa_auth_token on success
     */
    static async register(initData: string, sponsorLogin: string): Promise<TelegramRegisterResult> {
        return httpClient.post<TelegramRegisterResult>(
            '/telegram/mini-app/register',
            { sponsorLogin },
            { headers: signedMiniAppHeaders(initData) },
        )
    }

    static async emailInput(initData: string, text: string): Promise<TelegramAuthResult> {
        return httpClient.post<TelegramAuthResult>(
            '/telegram/mini-app/email-input',
            { text },
            { headers: signedMiniAppHeaders(initData) },
        )
    }

    static async emailAction(initData: string, action: `${TelegramEmailAction}:${string}`): Promise<TelegramAuthResult> {
        return httpClient.post<TelegramAuthResult>(
            '/telegram/mini-app/email-action',
            { action },
            { headers: signedMiniAppHeaders(initData) },
        )
    }

    /**
     * Verify TOTP code for 2FA authentication.
     *
     * Called when authenticate() returns totp_required.
     * Validates 6-digit code from authenticator app.
     *
     * Rate limiting: 5 attempts per 5 minutes, then blocked.
     *
     * @param spaAuthToken Token from authenticate() response
     * @param code 6-digit TOTP code
     * @returns Verification result with new spa_auth_token on success
     */
    static async verifyTotp(spaAuthToken: string, code: string): Promise<TelegramVerifyTotpResult> {
        return httpClient.post<TelegramVerifyTotpResult>(
            '/telegram/mini-app/verify-totp',
            { spa_auth_token: spaAuthToken, code },
            { headers: MINI_APP_HEADERS },
        )
    }

    /**
     * Exchange spa_auth_token for session token.
     *
     * The spa_auth_token is a one-time encrypted token (valid 60s).
     * This exchanges it for a permanent session token stored in localStorage.
     *
     * @param spaAuthToken Encrypted token from auth/register response
     * @returns Session token string to store in localStorage
     */
    static async exchangeToken(spaAuthToken: string, isCurrent?: () => boolean): Promise<string> {
        const response = isCurrent
            ? await httpClient.guardedRequest<ExchangeTokenResult>('/auth/exchange-token', isCurrent, {
                method: 'POST', body: JSON.stringify({ token: spaAuthToken }), headers: MINI_APP_HEADERS,
            })
            : await httpClient.post<ExchangeTokenResult>(
                '/auth/exchange-token',
                { token: spaAuthToken },
                { headers: MINI_APP_HEADERS },
            )

        // API returns: { result: 'success', data: { success: true, token: '...' } }
        const token = response?.data?.token ?? response?.hash ?? response?.token
        if (response?.result !== 'success' || response.data?.success === false || typeof token !== 'string' || !token.trim()) {
            throw new Error('exchange-token: invalid response')
        }
        return token
    }
}
